import { AuthContext } from "@/context/AuthContext";
import ConductionAPI from "@/javascript/conduction-api";
import { Classroom } from "@/javascript/user-objects";
import { Utils } from "@/javascript/utils";
import { Box, Button, Flex, Stack, Text, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconAlertCircle, IconCheck } from "@tabler/icons-react";
import { useContext } from "react";
import ConductionModal from "../ConductionModal";

interface ClassroomDeletionFormProps {
	classroom: Classroom;
	onClose: () => void;
	onDeleted: () => void;
}

const ClassroomDeletionForm: React.FC<ClassroomDeletionFormProps> = ({ classroom, onClose, onDeleted }) => {
	const auth = useContext(AuthContext);
	if (!auth) {
		throw new Error("AdminClassroomDeletionForm must be a child of AuthContext.");
	}
	const token = auth.token;
	if (!token) return null;

	// Set up the form; we only need one field where the user confirms the classroom name.
	const form = useForm({
		initialValues: {
			classroomName: "",
		},
		validate: {
			classroomName: (value) =>
				value.trim().length === 0
					? "You must type the classroom name to confirm deletion."
					: classroom !== null && value.trim() !== classroom.name.trim()
						? "Entered classroom name does not match."
						: null,
		},
	});

	const handleSubmit = async () => {
		if (!form.isValid()) {
			form.validate();
			return;
		}

		// Display a notification that deletion is in progress.
		const notificationId = Utils.displayNotification(
			"Deleting Classroom",
			"Deleting classroom, please wait...",
			"blue",
			"loading"
		);

		try {
			// Call the API to delete the classroom.
			await ConductionAPI.deleteClassroom(classroom.id, token);

			Utils.updateNotification(
				notificationId,
				"Classroom Deleted",
				"The classroom was deleted.",
				"green",
				<IconCheck size={16} />
			);
			// Close the modal or navigate away after deletion.
			onDeleted();
			onClose();
		} catch (error) {
			console.error("Error deleting classroom", error);
			Utils.updateNotification(
				notificationId,
				"Error",
				"Failed to delete the classroom.",
				"red",
				<IconAlertCircle size={16} />,
				true
			);
		}
	};

	const height = 20; // view height (vh)

	return (
		<ConductionModal
			onClose={onClose}
			title="Delete Classroom"
			size="sm"
		>
			<Box style={{ minHeight: `${height}vh` }}>
				<form onSubmit={form.onSubmit(handleSubmit)}>
					<Flex direction="column" justify="space-between" align="stretch" style={{ minHeight: `${height}vh` }}>
						<Stack>
							<Text>
								Please type the exact name of the classroom, "{classroom.name}", to confirm deletion:
							</Text>
							<TextInput
								label="Classroom Name"
								placeholder={classroom.name}
								required
								{...form.getInputProps("classroomName")}
							/>
						</Stack>
						<Button type="submit" radius="xl" size="sm" loading={form.submitting} color="red">
							Delete Classroom
						</Button>
					</Flex>
				</form>
			</Box>
		</ConductionModal>
	);
};

export default ClassroomDeletionForm;
